import { lazy, Suspense, useEffect } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { login, logout, tokenRefreshed, refreshAccessToken, getTokenExpiry } from "./store/auth-slice";
import { RootState } from "./store/store";
import { LoadingProvider } from "./LoadingContext";
import { ColorModeProvider } from "./ColorModeContext";

// Eager — present on every page load (homepage, deep-linked artist pages)
import Header from "./components/header/Header";
import Footer from "./components/home/Footer";
import Homepage from "./components/home/Homepage";
import Artist from "./components/artist/Artist";
import ScrollToTop from "./components/shared/ScrollToTop";
import PageViewTracker from "./components/shared/PageViewTracker";
import CookieConsentBanner from "./components/shared/CookieConsentBanner";
import SignupPromptModal from "./components/shared/SignupPromptModal";

// ─── Lazy route chunks ───────────────────────────────────────────────────────

const Auth = lazy(() => import(/* webpackChunkName: "auth" */ "./components/auth/Auth"));
const Calendar = lazy(() => import(/* webpackChunkName: "calendar" */ "./components/calendar/Calendar"));
const EventDetail = lazy(() => import(/* webpackChunkName: "event-detail" */ "./components/calendar/EventDetail"));
const SigningServices = lazy(() => import(/* webpackChunkName: "signing-services" */ "./components/signingservices/SigningServices"));
const AllCards = lazy(() => import(/* webpackChunkName: "all-cards", webpackPrefetch: true */ "./components/allcards/AllCards"));
const ArtistCardAnalysis = lazy(() => import(/* webpackChunkName: "artist-card-breakdown" */ "./components/artist/ArtistCardBreakdown"));
const RandomFlavorText = lazy(() => import(/* webpackChunkName: "random-flavor-text" */ "./components/randomflavortext/RandomFlavorText"));
const PrivacyPolicy = lazy(() => import(/* webpackChunkName: "privacy-policy" */ "./components/home/PrivacyPolicy"));
const TermsOfService = lazy(() => import(/* webpackChunkName: "terms-of-service" */ "./components/home/TermsOfService"));
const AffiliateDisclosure = lazy(() => import(/* webpackChunkName: "affiliate-disclosure" */ "./components/home/AffiliateDisclosure"));
const ContactPage = lazy(() => import(/* webpackChunkName: "contact" */ "./components/home/Contact"));
const Settings = lazy(() => import(/* webpackChunkName: "settings" */ "./components/settings/Settings"));
const Following = lazy(() => import(/* webpackChunkName: "following" */ "./components/settings/Following"));
const YourCards = lazy(() => import(/* webpackChunkName: "your-cards" */ "./components/settings/YourCards"));
const SigningTracker = lazy(() => import(/* webpackChunkName: "signing-tracker" */ "./components/signingtracker/SigningTracker"));
const ArtistSheet = lazy(() => import(/* webpackChunkName: "artist-sheet" */ "./components/artistsheet/ArtistSheet"));
// Group orders ("signing runs"): organizer setup and dashboard, invite links, and the participant view
const NewGroupOrder = lazy(() => import(/* webpackChunkName: "group-order-new" */ "./components/grouporders/NewGroupOrder"));
const OrganizerGroupOrder = lazy(() => import(/* webpackChunkName: "group-order-organizer" */ "./components/grouporders/OrganizerGroupOrder"));
const JoinGroupOrder = lazy(() => import(/* webpackChunkName: "group-order-join" */ "./components/grouporders/JoinGroupOrder"));
const MyGroupOrder = lazy(() => import(/* webpackChunkName: "group-order-shared" */ "./components/grouporders/MyGroupOrder"));
const Dashboard = lazy(() => import(/* webpackChunkName: "dashboard" */ "./components/dashboard/Dashboard"));
// Open to any signed-in user; the API only returns data to the verified artist or an admin
const ArtistDashboard = lazy(() => import(/* webpackChunkName: "artist-dashboard" */ "./components/artistdashboard/ArtistDashboard"));

// Admin-only — routes absent for non-admin sessions so chunks are never fetched
const AddArtist = lazy(() => import(/* webpackChunkName: "admin-add-artist" */ "./components/blogs/AddArtist"));
const EditArtist = lazy(() => import(/* webpackChunkName: "admin-edit-artist" */ "./components/blogs/EditArtist"));
// Hidden while the Bluesky post sync is off (webservice commit 7284679)
// const AdminPostReview = lazy(() => import(/* webpackChunkName: "admin-post-review" */ "./components/socialpostreview/AdminPostReview"));
const AddEvent = lazy(() => import(/* webpackChunkName: "admin-add-event" */ "./components/blogs/AddEvent"));
const AddArtistToEvent = lazy(() => import(/* webpackChunkName: "admin-add-artist-to-event" */ "./components/blogs/AddArtistToEvent"));
const RemoveArtistFromEvent = lazy(() => import(/* webpackChunkName: "admin-remove-artist-from-event" */ "./components/blogs/RemoveArtistFromEvent"));
const AnalyticsDashboard = lazy(() => import(/* webpackChunkName: "admin-analytics" */ "./components/analytics/AnalyticsDashboard"));
const SigningReportReview = lazy(() => import(/* webpackChunkName: "admin-signing-reports" */ "./components/signingreports/SigningReportReview"));
const AdminGroupOrders = lazy(() => import(/* webpackChunkName: "admin-group-orders" */ "./components/grouporders/AdminGroupOrders"));
const ScanEventArtists = lazy(() => import(/* webpackChunkName: "admin-scan-event-artists" */ "./components/blogs/ScanEventArtists"));

// ─── Suspense fallback ───────────────────────────────────────────────────────

// Reserves the viewport height so header/footer don't snap together while a
// chunk is in flight. No spinner — avoids a flash for fast connections.
function RouteFallback() {
  return <div style={{ minHeight: 'calc(100vh - 64px)' }} />;
}

// ─── App ─────────────────────────────────────────────────────────────────────

function App() {
  const isLoggedIn = useSelector((state: RootState) => state.auth.isLoggedIn);
  const authUser = useSelector((state: RootState) => state.auth.user);
  const isAdmin = authUser?.role === 'admin';
  const dispatch = useDispatch();

  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");

    if (storedToken && storedUser && !isLoggedIn) {
      const storedRefreshToken = localStorage.getItem('refreshToken') || '';
      dispatch(login({ token: storedToken, refreshToken: storedRefreshToken, user: JSON.parse(storedUser) }));
      return;
    }

    if (storedToken) {
      const exp = getTokenExpiry(storedToken);
      if (exp && Date.now() / 1000 > exp) {
        refreshAccessToken().then(newToken => {
          if (newToken) {
            dispatch(tokenRefreshed(newToken));
          } else {
            dispatch(logout());
          }
        });
      }
    }
  }, [dispatch, isLoggedIn]);

  return (
    <ColorModeProvider>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <LoadingProvider>
          <div>
            <ScrollToTop />
            <PageViewTracker />
            <CookieConsentBanner />
            <SignupPromptModal />
            <header>
              <Header />
            </header>
            <main>
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  <Route path="/" element={<Homepage />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/calendar" element={<Calendar />} />
                  <Route path="/calendar/:eventId" element={<EventDetail />} />
                  <Route path="/signingservices" element={<SigningServices />} />
                  {/* News is hidden for now; send old links and indexed URLs home */}
                  <Route path="/news/*" element={<Navigate to="/" replace />} />
                  <Route path="/artist/:name" element={<Artist />} />
                  {/* Linked from the new-artist digest email; the homepage is the artist list */}
                  <Route path="/artists" element={<Navigate to="/" replace />} />
                  <Route path="/allcards/:name" element={<AllCards />} />
                  <Route path="/artistcardbreakdown/:name" element={<ArtistCardAnalysis />} />                  
                  <Route path="/randomflavortext" element={<RandomFlavorText />} />
                  <Route path="/privacypolicy" element={<PrivacyPolicy />} />
                  <Route path="/termsofservice" element={<TermsOfService />} />
                  <Route path="/affiliate-disclosure" element={<AffiliateDisclosure />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/yourcards" element={<YourCards />} />
                  <Route path="/following" element={<Following />} />
                  <Route path="/signingtracker" element={<SigningTracker />} />
                  <Route path="/artistsheet" element={<ArtistSheet />} />
                  <Route path="/tracker/group/new" element={<NewGroupOrder />} />
                  <Route path="/tracker/group/:orderId" element={<OrganizerGroupOrder />} />
                  <Route path="/tracker/join/:token" element={<JoinGroupOrder />} />
                  <Route path="/tracker/shared/:orderId" element={<MyGroupOrder />} />
                  <Route path="/artist-dashboard" element={<ArtistDashboard />} />
                  <Route path="/artist-dashboard/:artistId" element={<ArtistDashboard />} />
                  {isAdmin && <>
                    <Route path="/add" element={<AddArtist />} />
                    <Route path="/editartist/:artistId" element={<EditArtist />} />
                    {/* <Route path="/reviewsocial" element={<AdminPostReview />} /> */}
                    <Route path="/addevent" element={<AddEvent />} />
                    <Route path="/addartisttoevent" element={<AddArtistToEvent />} />
                    <Route path="/removeartistfromevent" element={<RemoveArtistFromEvent />} />
                    <Route path="/analytics" element={<AnalyticsDashboard />} />
                    <Route path="/scaneventartists" element={<ScanEventArtists />} />
                    <Route path="/reviewsigningreports" element={<SigningReportReview />} />
                    <Route path="/admin/group-orders" element={<AdminGroupOrders />} />
                  </>}
                </Routes>
              </Suspense>
            </main>
            <footer>
              <Footer />
            </footer>
          </div>
        </LoadingProvider>
      </LocalizationProvider>
    </ColorModeProvider>
  );
}

export default App;
